import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  listLayoutsSchema,
  type ListCard,
  type ListLayout,
  type ListView,
} from '@forecastify/shared';
import { ListLayoutContext, type ListLayoutLabels, type ListLayoutStore } from '@forecastify/ui';
import { useT } from './i18n';
import { useSession } from './session';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · EVERY LIST'S LAYOUT, READ ONCE AND SAVED AS IT CHANGES.     │
 * │ decisions.md LV-1, `0224`.                                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The kit's `DataList` reads its own entry from this by its `columnsKey`; this is the one place
 * that talks to `/workspace/list-layouts`. All of the member's layouts arrive in one read at start —
 * a few dozen rows at most — so opening a list never waits on a request of its own.
 *
 * ⚠️ **Optimistic, and nothing to roll back to on failure** (`state-rules.md` §7 asks for a revert
 * on a refused write): the change is the person's own display, on their screen, and a failed save
 * leaves it right where they put it for this session. Saying *"your card could not be saved"* in a
 * toast for a preference would be louder than the thing it is about — the next start shows the last
 * saved layout, which is the honest outcome.
 */
export function ListLayoutsProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const session = useSession();
  const t = useT();
  const [layouts, setLayouts] = useState<ReadonlyMap<string, ListLayout>>(new Map());

  useEffect(() => {
    if (session.workspaceId === null) return;
    let live = true;
    void session
      .request('/workspace/list-layouts')
      .then(async (response) => {
        if (!response.ok) return;
        const parsed = listLayoutsSchema.safeParse(await response.json());
        if (parsed.success && live) {
          setLayouts(new Map(parsed.data.data.map((one) => [one.listKey, one])));
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the workspace is what the layouts belong to. */
  }, [session.workspaceId]);

  const save = useCallback(
    (
      key: string,
      patch: {
        readonly view?: ListView | null;
        readonly mobileView?: ListView | null;
        readonly card?: ListCard | null;
      },
    ) => {
      setLayouts((current) => {
        const next = new Map(current);
        const was = current.get(key) ?? { listKey: key, view: null, mobileView: null, card: null };
        next.set(key, {
          listKey: key,
          view: patch.view === undefined ? was.view : patch.view,
          mobileView: patch.mobileView === undefined ? was.mobileView : patch.mobileView,
          card: patch.card === undefined ? was.card : patch.card,
        });
        return next;
      });
      void session
        .request(`/workspace/list-layouts/${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: JSON.stringify(patch),
        })
        .catch(() => undefined);
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- see above. */
    [session.workspaceId],
  );

  const labels: ListLayoutLabels = useMemo(
    () => ({
      view: t('listLayout.view'),
      list: t('listLayout.list'),
      cards: t('listLayout.cards'),
      settings: t('listLayout.settings'),
      badge: t('listLayout.badge'),
      avatar: t('listLayout.avatar'),
      detail: (index: number) => t('listLayout.detail', { index }),
      footer: t('listLayout.footer'),
      none: t('listLayout.none'),
      reset: t('listLayout.reset'),
      done: t('listLayout.done'),
      sort: t('listLayout.sort'),
      title: t('listLayout.title'),
      context: t('listLayout.context'),
      preview: t('listLayout.preview'),
      close: t('common.close'),
    }),
    [t],
  );

  const store: ListLayoutStore = useMemo(
    () => ({
      layoutOf: (key: string) => {
        const one = layouts.get(key);
        return {
          view: one?.view ?? null,
          mobileView: one?.mobileView ?? null,
          card: one?.card ?? null,
        };
      },
      save,
      labels,
    }),
    [layouts, save, labels],
  );

  return <ListLayoutContext.Provider value={store}>{children}</ListLayoutContext.Provider>;
}
